const state = {
  visitedViews: [],
  cachedViews: [],
  /** 各页面缓存失效次数，用于 app-main 的 :key，实现关闭 / 刷新后只重建当前页面 */
  cacheEpochs: {},
};

/**
 * 从当前路由对象中提取可缓存的路由名称列表。
 * 优先使用 matched 链路，确保父子嵌套路由都能被 keep-alive 命中。
 * @param {Object} view 当前路由对象（this.$route）
 * @returns {string[]}
 */
function getCacheNames(view) {
  const matched = Array.isArray(view.matched) ? view.matched : [];
  const matchedNames = matched
    .filter(route => route && route.name && !(route.meta && route.meta.noCache))
    .map(route => route.name);

  if (matchedNames.length > 0) {
    return matchedNames;
  }

  if (view.name && !(view.meta && view.meta.noCache)) {
    return [view.name];
  }

  return [];
}

const mutations = {
  ADD_VISITED_VIEW: (state, view) => {
    if (state.visitedViews.some(v => v.path === view.path)) {
      return;
    }

    state.visitedViews.push(
      Object.assign({}, view, {
        title: view.meta.title || 'no-name',
      })
    );
  },
  ADD_CACHED_VIEW: (state, view) => {
    getCacheNames(view).forEach(name => {
      if (!state.cachedViews.includes(name)) {
        state.cachedViews.push(name);
      }
    });
  },

  DEL_VISITED_VIEW: (state, view) => {
    for (const [i, v] of state.visitedViews.entries()) {
      if (v.path === view.path) {
        state.visitedViews.splice(i, 1);
        break;
      }
    }
  },
  DEL_CACHED_VIEW: (state, view) => {
    /**
     * 只移除被关闭页面自身的缓存名。
     * 父级容器名（如 MaterialLibrary）必须保留：外层 keep-alive 缓存的就是容器组件，
     * 一旦容器名被移出 include，容器会被整体剪枝销毁，容器内部 keep-alive 缓存的其它页面会一起被清掉。
     */
    const names = getCacheNames(view);
    const name = names[names.length - 1];
    const index = name ? state.cachedViews.indexOf(name) : -1;
    if (index > -1) {
      state.cachedViews.splice(index, 1);
    }
    // 由于容器名不能移除，外层 keep-alive 里该页面独占的容器实例不会自动释放，
    // 通过递增 epoch 让 app-main 的 :key 变化，实现关闭 / 刷新后只重建当前页面。
    if (name) {
      state.cacheEpochs = {
        ...state.cacheEpochs,
        [name]: (state.cacheEpochs[name] || 0) + 1,
      };
    }
  },

  DEL_OTHERS_VISITED_VIEWS: (state, view) => {
    state.visitedViews = state.visitedViews.filter(v => {
      return v.meta.affix || v.path === view.path;
    });
  },
  DEL_OTHERS_CACHED_VIEWS: (state, view) => {
    const cacheNames = getCacheNames(view);
    state.cachedViews = state.cachedViews.filter(name => cacheNames.includes(name));
  },

  DEL_ALL_VISITED_VIEWS: state => {
    // keep affix tags
    const affixTags = state.visitedViews.filter(tag => tag.meta.affix);
    state.visitedViews = affixTags;
  },
  DEL_ALL_CACHED_VIEWS: state => {
    state.cachedViews = [];
  },

  UPDATE_VISITED_VIEW: (state, view) => {
    for (let v of state.visitedViews) {
      if (v.path === view.path) {
        v = Object.assign(v, view);
        break;
      }
    }
  },
};

const actions = {
  addView({ dispatch }, view) {
    dispatch('addVisitedView', view);
    dispatch('addCachedView', view);
  },
  addVisitedView({ commit }, view) {
    commit('ADD_VISITED_VIEW', view);
  },
  addCachedView({ commit }, view) {
    commit('ADD_CACHED_VIEW', view);
  },

  delView({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delVisitedView', view);
      dispatch('delCachedView', view);
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews],
      });
    });
  },
  delVisitedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_VISITED_VIEW', view);
      resolve([...state.visitedViews]);
    });
  },
  delCachedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_CACHED_VIEW', view);
      resolve([...state.cachedViews]);
    });
  },

  delOthersViews({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delOthersVisitedViews', view);
      dispatch('delOthersCachedViews', view);
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews],
      });
    });
  },
  delOthersVisitedViews({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_VISITED_VIEWS', view);
      resolve([...state.visitedViews]);
    });
  },
  delOthersCachedViews({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_CACHED_VIEWS', view);
      resolve([...state.cachedViews]);
    });
  },

  delAllViews({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delAllVisitedViews', view);
      dispatch('delAllCachedViews', view);
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews],
      });
    });
  },
  delAllVisitedViews({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_VISITED_VIEWS');
      resolve([...state.visitedViews]);
    });
  },
  delAllCachedViews({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_CACHED_VIEWS');
      resolve([...state.cachedViews]);
    });
  },

  updateVisitedView({ commit }, view) {
    commit('UPDATE_VISITED_VIEW', view);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
